import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DateTimePicker.css';
import FormError from './ui/FormError';

interface DateTimePickerProps {
  id: string;
  label: string;
  value: string; // ISO datetime-local format: "2025-01-27T14:30"
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}

interface ScrollableSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  hasError?: boolean;
  ariaLabel: string;
}

// Custom scrollable select component
const ScrollableSelect: React.FC<ScrollableSelectProps> = ({
  value,
  options,
  onChange,
  hasError,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Scroll to selected value when opening
  useEffect(() => {
    if (isOpen && listRef.current && selectedRef.current) {
      const list = listRef.current;
      const selected = selectedRef.current;
      const listRect = list.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();

      // Center the selected item in the list
      const scrollTop = selected.offsetTop - (listRect.height / 2) + (selectedRect.height / 2);
      list.scrollTop = Math.max(0, scrollTop);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const currentIndex = options.indexOf(value);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < options.length - 1) {
          onChange(options[currentIndex + 1]);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          onChange(options[currentIndex - 1]);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  }, [isOpen, options, value, onChange]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div
      className="scrollable-select"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={`scrollable-select__trigger${hasError ? ' scrollable-select__trigger--error' : ''}${isOpen ? ' scrollable-select__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {value}
      </button>

      {isOpen && (
        <div className="scrollable-select__dropdown" role="listbox" ref={listRef}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              ref={option === value ? selectedRef : null}
              className={`scrollable-select__option${option === value ? ' scrollable-select__option--selected' : ''}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={option === value}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  id,
  label,
  value,
  onChange,
  required = false,
  error,
}) => {
  // Parse the datetime-local value into separate date and time
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');

  useEffect(() => {
    if (value) {
      // Check if it's an ISO string with timezone (contains Z or +/-)
      const isISOWithTZ = value.includes('Z') || /[+-]\d{2}:\d{2}$/.test(value);

      if (isISOWithTZ) {
        // Parse as ISO and extract local time components
        const dateObj = new Date(value);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        setDate(`${year}-${month}-${day}`);
        setHour(String(dateObj.getHours()).padStart(2, '0'));
        setMinute(String(dateObj.getMinutes()).padStart(2, '0'));
      } else {
        // datetime-local format: "YYYY-MM-DDTHH:MM" - parse directly
        const [datePart, timePart] = value.split('T');
        setDate(datePart || '');
        if (timePart) {
          const [h, m] = timePart.split(':');
          setHour(h || '12');
          setMinute(m || '00');
        }
      }
    }
  }, [value]);

  const updateDateTime = (newDate: string, newHour: string, newMinute: string) => {
    if (newDate) {
      // Create a proper Date object in local time and convert to ISO string
      // This ensures the server receives the correct UTC time regardless of timezone
      const [year, month, day] = newDate.split('-').map(Number);
      const localDate = new Date(year, month - 1, day, parseInt(newHour), parseInt(newMinute));
      onChange(localDate.toISOString());
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setDate(newDate);
    updateDateTime(newDate, hour, minute);
  };

  const handleHourChange = (newHour: string) => {
    setHour(newHour);
    updateDateTime(date, newHour, minute);
  };

  const handleMinuteChange = (newMinute: string) => {
    setMinute(newMinute);
    updateDateTime(date, hour, newMinute);
  };

  // Generate hour options (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  // Generate minute options (00, 05, 10, ..., 55)
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  const errorStyle: React.CSSProperties = error ? {
    borderColor: 'var(--danger, #dc3545)',
  } : {};

  return (
    <div className={`datetime-picker${error ? ' datetime-picker--error' : ''}`}>
      <label className="datetime-picker__label">
        {label}
        {required && <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '0.25rem' }} aria-hidden="true">*</span>}
      </label>
      <div className="datetime-picker__inputs">
        <div className="datetime-picker__date">
          <input
            type="date"
            id={`${id}-date`}
            value={date}
            onChange={handleDateChange}
            className="datetime-picker__date-input"
            style={errorStyle}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <div className={`datetime-picker__time${error ? ' datetime-picker__time--error' : ''}`}>
          <ScrollableSelect
            value={hour}
            options={hours}
            onChange={handleHourChange}
            hasError={!!error}
            ariaLabel="Hour"
          />
          <span className="datetime-picker__separator">:</span>
          <ScrollableSelect
            value={minute}
            options={minutes}
            onChange={handleMinuteChange}
            hasError={!!error}
            ariaLabel="Minute"
          />
        </div>
      </div>
      {error && <FormError id={`${id}-error`} message={error} />}
    </div>
  );
};

export default DateTimePicker;
